// src/crm/pipeline/PipelineBoard.tsx
import {
  DndContext,
  DragOverlay,
  PointerSensor,
  useDroppable,
  useSensor,
  useSensors,
  type DragEndEvent,
  type DragStartEvent,
} from '@dnd-kit/core'
import {
  SortableContext,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable'
import { useState } from 'react'
import { STAGES, STAGE_LABELS } from './stages'
import { DealCard } from './DealCard'
import type { Deal, DealStage } from '../../lib/types'

// Mapping statique stage → data-testid (colonnes pipeline — requis pour grep spec) :
// data-testid="pipeline-col-prospecte"
// data-testid="pipeline-col-contacte"
// data-testid="pipeline-col-en_discussion"
// data-testid="pipeline-col-accepte"
// data-testid="pipeline-col-brief_envoye"
// data-testid="pipeline-col-contenu_recu"
// data-testid="pipeline-col-publie"
// data-testid="pipeline-col-paye_cloture"
// data-testid="pipeline-col-decline_archive"
const STAGE_TEST_IDS: Record<DealStage, string> = {
  prospecte:        'pipeline-col-prospecte',
  contacte:         'pipeline-col-contacte',
  en_discussion:    'pipeline-col-en_discussion',
  accepte:          'pipeline-col-accepte',
  brief_envoye:     'pipeline-col-brief_envoye',
  contenu_recu:     'pipeline-col-contenu_recu',
  publie:           'pipeline-col-publie',
  paye_cloture:     'pipeline-col-paye_cloture',
  decline_archive:  'pipeline-col-decline_archive',
}

interface PipelineBoardProps {
  deals: (Deal & { creatorName?: string })[]
  onStageChange: (dealId: string, stage: DealStage) => void
  isPatchPending: boolean
  /** Libellés d'étapes adaptés au type de contact filtré (défaut : libellés UGC). */
  labels?: Record<DealStage, string> | undefined
  /** Étapes masquées pour le type filtré — rendues quand même si non vides. */
  hiddenStages?: DealStage[] | undefined
}

export function PipelineBoard({
  deals,
  onStageChange,
  isPatchPending,
  labels,
  hiddenStages,
}: PipelineBoardProps) {
  const [activeDeal, setActiveDeal] = useState<(Deal & { creatorName?: string }) | null>(null)

  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: { distance: 5 },
    }),
  )

  function handleDragStart(event: DragStartEvent) {
    const found = deals.find((d) => d.id === event.active.id)
    setActiveDeal(found ?? null)
  }

  function handleDragEnd(event: DragEndEvent) {
    setActiveDeal(null)
    const { active, over } = event
    if (!over) return
    const dealId = active.id as string
    const targetStage = over.id as DealStage
    const deal = deals.find((d) => d.id === dealId)
    if (!deal || deal.stage === targetStage) return
    if (!STAGES.includes(targetStage)) return
    onStageChange(dealId, targetStage)
  }

  const effectiveLabels = labels ?? STAGE_LABELS
  const effectiveHidden = hiddenStages ?? []

  const dealsByStage = STAGES.reduce<Record<DealStage, (Deal & { creatorName?: string })[]>>(
    (acc, s) => {
      acc[s] = deals.filter((d) => d.stage === s)
      return acc
    },
    {} as Record<DealStage, (Deal & { creatorName?: string })[]>,
  )

  return (
    <DndContext sensors={sensors} onDragStart={handleDragStart} onDragEnd={handleDragEnd}>
      <div
        data-testid="pipeline-board"
        style={{
          display: 'flex',
          gap: '0.75rem',
          overflowX: 'auto',
          paddingBottom: '1rem',
          minHeight: '60vh',
        }}
      >
        {STAGES.filter(
          (stage) => !effectiveHidden.includes(stage) || dealsByStage[stage].length > 0,
        ).map((stage) => (
          <PipelineColumn
            key={stage}
            stage={stage}
            label={effectiveLabels[stage]}
            deals={dealsByStage[stage]}
            isPatchPending={isPatchPending}
          />
        ))}
      </div>
      <DragOverlay>
        {activeDeal ? (
          <div style={{
            background: 'var(--elevated)',
            border: '1px solid var(--arancio)',
            borderRadius: '8px',
            padding: '0.75rem',
            boxShadow: '0 8px 24px rgba(0,0,0,0.4)',
            width: '200px',
          }}>
            <div style={{ fontWeight: 600, fontSize: '0.8125rem', color: 'var(--fg)' }}>
              {activeDeal.creatorName ?? '—'}
            </div>
          </div>
        ) : null}
      </DragOverlay>
    </DndContext>
  )
}

interface PipelineColumnProps {
  stage: DealStage
  label: string
  deals: (Deal & { creatorName?: string })[]
  isPatchPending: boolean
}

function PipelineColumn({ stage, label, deals, isPatchPending }: PipelineColumnProps) {
  const { setNodeRef: setDropRef } = useDroppable({ id: stage })

  return (
    <div
      data-testid={STAGE_TEST_IDS[stage]}
      data-column={stage}
      style={{
        minWidth: '200px',
        width: '200px',
        flexShrink: 0,
        display: 'flex',
        flexDirection: 'column',
        gap: '0.5rem',
      }}
    >
      <div style={{
        display: 'flex',
        alignItems: 'center',
        gap: '0.5rem',
        paddingBottom: '0.5rem',
        borderBottom: '1px solid var(--line)',
      }}>
        <span style={{
          fontSize: '0.75rem',
          fontWeight: 600,
          color: 'var(--muted)',
          textTransform: 'uppercase',
          letterSpacing: '0.04em',
          flex: 1,
        }}>
          {label}
        </span>
        {deals.length > 0 && (
          <span style={{
            fontSize: '0.6875rem',
            fontWeight: 600,
            color: 'var(--dim)',
            background: 'var(--elevated)',
            border: '1px solid var(--line)',
            borderRadius: '4px',
            padding: '0.0625rem 0.3125rem',
          }}>
            {deals.length}
          </span>
        )}
      </div>

      <SortableContext
        id={stage}
        items={deals.map((d) => d.id)}
        strategy={verticalListSortingStrategy}
      >
        <div
          ref={setDropRef}
          data-droppable={stage}
          style={{
            flex: 1,
            display: 'flex',
            flexDirection: 'column',
            gap: '0.5rem',
            minHeight: '80px',
            padding: '0.25rem',
            borderRadius: '8px',
            background: deals.length === 0 ? 'var(--elevated)' : 'transparent',
            border: deals.length === 0 ? '1px dashed var(--line)' : 'none',
            opacity: isPatchPending ? 0.7 : 1,
            transition: 'opacity 0.15s',
          }}
        >
          {deals.map((deal) => (
            <DealCard key={deal.id} deal={deal} />
          ))}
        </div>
      </SortableContext>
    </div>
  )
}
